<template>
  <section>
    <b-tabs
      v-model="currentTabIndex"
      :animated="false"
      :class="`sticky-tabs-headers ${lowerTab ? 'lower' : ''}`"
    >
      <b-tab-item :label="$t('profilePage.etablissement.identite')">
        <template #header>
          <TabHeaderWithHint
            :label="$t('profilePage.etablissement.identite')"
            :tab-validation-hint="tabValidationHints[0]"
          />
        </template>
        <ValidationObserver ref="observer-0">
          <EtablissementIdentiteDetails
            :etablissement="etablissement"
            :user-can-change="userCanChange"
            :readonly="readonly"
            :validation-group="validationGroup"
            @update-etablissement="updateEtablissement"
            @change-etablissement="changeEtablissement"
          />
        </ValidationObserver>
      </b-tab-item>

      <b-tab-item>
        <template #header>
          <TabHeaderWithHint
            :label="$t('profilePage.etablissement.contact')"
            :tab-validation-hint="tabValidationHints[1]"
          />
        </template>
        <ValidationObserver ref="observer-1">
          <EtablissementContactDetails
            :etablissement="etablissement"
            :user-can-change="userCanChange"
            :readonly="readonly"
            :validation-group="validationGroup"
            @update-etablissement="updateEtablissement"
          />
        </ValidationObserver>
      </b-tab-item>

      <b-tab-item>
        <template #header>
          <TabHeaderWithHint
            :label="$t('profilePage.etablissement.chef')"
            :tab-validation-hint="tabValidationHints[2]"
          />
        </template>
        <ValidationObserver ref="observer-2">
          <EtablissementChefDetails
            :etablissement="etablissement"
            :user-can-change="userCanChange"
            :readonly="readonly"
            :validation-group="validationGroup"
            @update-etablissement="updateEtablissement"
          />
        </ValidationObserver>
      </b-tab-item>

      <b-tab-item>
        <template #header>
          <TabHeaderWithHint
            :label="$t('profilePage.etablissement.siteWeb')"
            :tab-validation-hint="tabValidationHints[3]"
          />
        </template>
        <ValidationObserver ref="observer-3">
          <EtablissementSiteWebDetails
            :etablissement="etablissement"
            :user-can-change="userCanChange"
            :readonly="readonly"
            :validation-group="validationGroup"
            @update-etablissement="updateEtablissement"
          />
        </ValidationObserver>
      </b-tab-item>
    </b-tabs>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { t } from "@/utils/i18n-utils"
import ValidationResult, { TabValidationHint, VALIDATION_TYPES } from "@/model/ValidationResult"

import EtablissementIdentiteDetails from "@/views/organisme/EtablissementIdentiteDetails.vue"
import EtablissementContactDetails from "@/views/organisme/EtablissementContactDetails.vue"
import EtablissementSiteWebDetails from "@/views/organisme/EtablissementSiteWebDetails.vue"
import EtablissementChefDetails from "@/views/organisme/EtablissementChefDetails.vue"
import { EtablissementDTO } from "@/model/bean/GeneratedDTOs"
import TabHeaderWithHint from "@/views/commons/TabHeaderWithHint.vue"
import { BTabs, BTabItem } from "buefy"

export default defineComponent({
  name: "EtablissementView",
  components: {
    EtablissementContactDetails,
    EtablissementSiteWebDetails,
    EtablissementChefDetails,
    EtablissementIdentiteDetails,
    ValidationObserver,
    TabHeaderWithHint,
    BTabs,
    BTabItem,
  },
  props: {
    etablissement: {
      type: Object as PropType<EtablissementDTO>,
      required: true,
    },
    userCanChange: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    validationGroup: {
      type: String,
      default: "",
    },
    lowerTab: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["change-etablissement", "update-etablissement"],
  data() {
    return {
      numberOfTabs: 0,
      currentTabIndex: 0,
      tabValidationHints: [] as TabValidationHint[],
    }
  },
  watch: {
    currentTabIndex(): void {
      this.setWarnings()
    },
  },
  created(): void {
    this.numberOfTabs = 4
    this.currentTabIndex = 0
    for (let i = 0; i < this.numberOfTabs; i++) {
      this.tabValidationHints.push(new TabValidationHint())
    }
  },
  mounted(): void {
    this.setWarnings()
  },
  updated(): void {
    this.setWarnings()
  },
  methods: {
    changeEtablissement(): void {
      this.$emit("change-etablissement")
    },

    updateEtablissement(etablissement: EtablissementDTO): void {
      this.setWarnings()
      this.$emit("update-etablissement", etablissement)
    },

    // Methods from AbstractFormStepWithTabs
    setTabIndex(tabIndex: number): void {
      this.currentTabIndex = tabIndex
    },

    handleTabWarnings(tabIndex: number): void {
      const observer = this.$refs[`observer-${tabIndex}`]
      if (!observer) {
        return
      }
      //@ts-ignore
      return observer.validateWithInfo().then((validationResult: ValidationResult) => {
        const tabValidationHint = new TabValidationHint()
        const errors = Object.values(validationResult.errors).flat()
        tabValidationHint.errorsCount = errors.length
        tabValidationHint.type = VALIDATION_TYPES.WARNING

        if (errors.some((err) => !this.isWarning(err as string))) {
          tabValidationHint.type = VALIDATION_TYPES.DANGER
        }
        this.tabValidationHints[tabIndex] = tabValidationHint
      })
    },

    setWarnings(): void {
      for (let i = 0; i < this.numberOfTabs; i++) {
        this.handleTabWarnings(i)
      }
    },

    isWarning(errorMessage: string): boolean {
      return errorMessage == t("validation-input-invalid-required")
    },

    getNumberOfTabs(): number {
      return this.numberOfTabs
    },

    lastTabDisplayed(): boolean {
      return this.currentTabIndex === this.numberOfTabs - 1
    },

    firstTabDisplayed(): boolean {
      return this.currentTabIndex === 0
    },

    goToNextTab(): void {
      const nextIndex = this.lastTabDisplayed() ? this.currentTabIndex : this.currentTabIndex + 1
      this.setTabIndex(nextIndex)
    },

    goToPreviousTab(): void {
      const previousIndex = this.firstTabDisplayed()
        ? this.currentTabIndex
        : this.currentTabIndex - 1
      this.setTabIndex(previousIndex)
    },

    getNumberOfWarningsOnAllTabs(): number {
      if (!this.tabValidationHints || this.tabValidationHints.length === 0) {
        return 0
      }
      let errors = 0
      this.tabValidationHints.forEach((element) => {
        errors += element.errorsCount
      })
      return errors
    },

    getNumberOfWarningsOnAllTabsForStructureDetail(): number {
      this.tabValidationHints = []
      this.setWarnings()
      if (!this.tabValidationHints || this.tabValidationHints.length === 0) {
        return 0
      }
      let errors = 0
      this.tabValidationHints.forEach((element) => {
        errors += element.errorsCount
      })
      return errors
    },
  },
})
</script>
